import React, { useState, useEffect } from 'react';
import { authorizedFetch } from '../utils';
import { FTSelect } from './inputs';

const NO_PLACEMENT_EXPERIENCE = {
  id: '',
  name: 'No placement experiences found'
};

const NO_PLACEMENT_LOCATION = {
  id: '',
  name: 'No placement locations found'
};

export const getPlacementExperiences = async query => {
  return await authorizedFetch(`/api/PlacementExperience/search`, 'POST', {
    query: query,
    from: 0,
    size: 10000,
    sort: {
      dateModified: 'desc'
    }
  }).then(response => {
    return response.items ?? [];
  });
};

export const getPlacementLocations = async query => {
  return await authorizedFetch(`/api/PlacementLocation/search`, 'POST', {
    query: query,
    from: 0,
    size: 10000,
    sort: {
      dateModified: 'desc'
    }
  }).then(response => {
    return response.items ?? [];
  });
};

const PlacementExperience = ({
  placementExperienceId = '',
  placementLocationId = '',
  onChange,
  disabled,
  placementExperienceTipContent = 'The underlying user experience this placement is part of.',
  placementLocationTipContent = 'A reference to the specific job of the page that the placement exists on.',
  addError,
  hideLocation = false
}) => {
  const [placementExperiences, setPlacementExperiences] = useState([]);
  const [placementLocations, setPlacementLocations] = useState([]);
  const [
    filteredPlacementExperiences,
    setFilteredPlacementExperiences
  ] = useState([]);
  const [filteredPlacementLocations, setFilteredPlacementLocations] = useState(
    []
  );
  const [
    selectedPlacementExperience,
    setSelectedPlacementExperience
  ] = useState(
    placementExperiences.find(pe => pe.id === placementExperienceId)
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const fetchedPlacementExperiences = await getPlacementExperiences();
        if (
          Array.isArray(fetchedPlacementExperiences) &&
          fetchedPlacementExperiences.length > 0
        ) {
          setPlacementExperiences([...fetchedPlacementExperiences]);
          setFilteredPlacementExperiences([...fetchedPlacementExperiences]);

          const loadedPlacementExperience = fetchedPlacementExperiences.find(
            pe => pe.id === placementExperienceId
          );
          setSelectedPlacementExperience(loadedPlacementExperience);

          if (!loadedPlacementExperience && placementExperienceId?.length > 0) {
            onChange({
              placementExperienceId: null,
              placementLocationId: null
            });
            addError(
              `A placement experience with an unmatched ID was found. Please select a new placement experience.`
            );
          }
        } else {
          setPlacementExperiences(
            placementExperienceId
              ? [
                  {
                    id: placementExperienceId,
                    name: 'No placement experiences found'
                  }
                ]
              : [NO_PLACEMENT_EXPERIENCE]
          );
          setFilteredPlacementExperiences(
            placementExperienceId
              ? [
                  {
                    id: placementExperienceId,
                    name: 'No placement experiences found'
                  }
                ]
              : [NO_PLACEMENT_EXPERIENCE]
          );
        }

        // Only fetch placement locations if we have a valid placement experience ID
        if (
          placementExperienceId &&
          placementExperienceId.length > 0 &&
          hideLocation === false
        ) {
          const fetchedPlacementLocations = await getPlacementLocations(
            placementExperienceId
          );
          if (
            Array.isArray(fetchedPlacementLocations) &&
            fetchedPlacementLocations.length > 0
          ) {
            setPlacementLocations([...fetchedPlacementLocations]);
          } else {
            setPlacementLocations(
              placementLocationId
                ? [
                    {
                      id: placementLocationId,
                      name: 'No placement locations found'
                    }
                  ]
                : [NO_PLACEMENT_LOCATION]
            );
          }
        } else {
          setPlacementLocations([NO_PLACEMENT_LOCATION]);
        }
      } catch (err) {
        addError(`There was an error fetching placement data`, err.message);
        return null;
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch placement locations when placement experience changes
  useEffect(() => {
    const loadPlacementLocations = async () => {
      if (
        selectedPlacementExperience &&
        selectedPlacementExperience.id &&
        hideLocation === false
      ) {
        try {
          const fetchedPlacementLocations = await getPlacementLocations(
            selectedPlacementExperience.id
          );
          if (
            Array.isArray(fetchedPlacementLocations) &&
            fetchedPlacementLocations.length > 0
          ) {
            setPlacementLocations([...fetchedPlacementLocations]);
          } else {
            setPlacementLocations([NO_PLACEMENT_LOCATION]);
          }
        } catch (err) {
          addError(
            `There was an error fetching placement locations`,
            err.message
          );
          setPlacementLocations([NO_PLACEMENT_LOCATION]);
        }
      } else {
        setPlacementLocations([NO_PLACEMENT_LOCATION]);
      }
    };

    loadPlacementLocations();
  }, [selectedPlacementExperience, addError]);

  useEffect(() => {
    if (!!selectedPlacementExperience) {
      // Filter placement locations based on the selected placement experience
      const filteredLocations = placementLocations.filter(location => {
        // If the placement experience has a placementExperienceId property, filter by that
        if (location.placementExperienceId) {
          return (
            location.placementExperienceId === selectedPlacementExperience.id
          );
        }
        // If no placementExperienceId property, show all locations
        return true;
      });

      setFilteredPlacementLocations(filteredLocations);
    } else {
      setFilteredPlacementLocations([]);
    }
  }, [selectedPlacementExperience, placementLocations]);

  return (
    <>
      <FTSelect
        disabled={disabled}
        title={`Placement Experience`}
        placeholder={`No placement experience selected`}
        labelKey='name'
        valueKey='id'
        options={filteredPlacementExperiences}
        value={placementExperiences.find(pe => pe.id === placementExperienceId)}
        label={'Placement Experience'}
        tipContent={placementExperienceTipContent}
        onChange={({ id }) => {
          onChange({ placementExperienceId: id, placementLocationId: null });
          setSelectedPlacementExperience(
            placementExperiences.find(pe => pe.id === id)
          );
        }}
        handleOpen={() => {
          setFilteredPlacementExperiences(placementExperiences);
        }}
        handleSearch={search => {
          const lowercaseSearch = search.toLowerCase();
          if (search === '') {
            setFilteredPlacementExperiences(placementExperiences);
          } else {
            setFilteredPlacementExperiences(
              placementExperiences.filter(pe => {
                const lowercaseLabel = pe.name.toLowerCase();
                return lowercaseLabel.includes(lowercaseSearch);
              })
            );
          }
        }}
      />
      {!hideLocation && (
        <FTSelect
          disabled={!selectedPlacementExperience || disabled}
          title={`Placement Location`}
          placeholder={
            !!selectedPlacementExperience
              ? `No placement location selected`
              : 'Select a placement experience first'
          }
          labelKey='name'
          valueKey='id'
          options={filteredPlacementLocations}
          value={placementLocations.find(pl => pl.id === placementLocationId)}
          label={'Placement Location'}
          tipContent={placementLocationTipContent}
          onChange={({ id }) =>
            onChange({
              placementExperienceId: selectedPlacementExperience.id,
              placementLocationId: id
            })
          }
          handleOpen={() => {
            setFilteredPlacementLocations(
              placementLocations.filter(location => {
                if (location.placementExperienceId) {
                  return (
                    location.placementExperienceId ===
                    selectedPlacementExperience?.id
                  );
                }
                return true;
              })
            );
          }}
          handleSearch={search => {
            const lowercaseSearch = search.toLowerCase();
            if (search === '') {
              setFilteredPlacementLocations(
                placementLocations.filter(location => {
                  if (location.placementExperienceId) {
                    return (
                      location.placementExperienceId ===
                      selectedPlacementExperience?.id
                    );
                  }
                  return true;
                })
              );
            } else {
              setFilteredPlacementLocations(
                placementLocations.filter(pl => {
                  const lowercaseLabel = pl.name.toLowerCase();
                  const matchesSearch = lowercaseLabel.includes(
                    lowercaseSearch
                  );
                  const matchesExperience =
                    !pl.placementExperienceId ||
                    pl.placementExperienceId ===
                      selectedPlacementExperience?.id;
                  return matchesSearch && matchesExperience;
                })
              );
            }
          }}
        />
      )}
    </>
  );
};

export default PlacementExperience;
